import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Plus, Trash, CheckCircle, X, CalendarBlank, Path } from "@phosphor-icons/react";

const STATUS_PILL = (s) => (s === "active" ? "pill-online" : s === "pending" ? "pill-warn" : "pill-offline");
const emptyForm = { name: "", description: "", price: "", billing_type: "monthly", duration_days: 30, trips_included: 10, is_active: true };

export function SubscriptionsTab({ users }) {
  const drivers = (users || []).filter((u) => u.role === "driver");
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [required, setRequired] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [assign, setAssign] = useState({ driver_id: "", plan_id: "" });

  const refresh = useCallback(async () => {
    const [p, s, st] = await Promise.all([
      api.get("/admin/plans"),
      api.get("/admin/subscriptions"),
      api.get("/admin/settings"),
    ]);
    setPlans(p.data); setSubs(s.data); setRequired(st.data.subscriptions_required);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleRequired = async () => {
    const v = !required;
    setRequired(v);
    await api.put("/admin/settings", { subscriptions_required: v });
  };

  const submitPlan = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      name: form.name,
      description: form.description || null,
      price: parseFloat(form.price),
      billing_type: form.billing_type,
      duration_days: form.billing_type === "monthly" ? parseInt(form.duration_days, 10) : null,
      trips_included: form.billing_type === "per_trips" ? parseInt(form.trips_included, 10) : null,
      is_active: form.is_active,
    };
    try {
      if (editingId) await api.put(`/admin/plans/${editingId}`, payload);
      else await api.post("/admin/plans", payload);
      setForm(emptyForm); setEditingId(null);
      refresh();
    } catch (err) { setError(err.response?.data?.detail || "Error al guardar el plan"); }
  };

  const editPlan = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, description: p.description || "", price: p.price, billing_type: p.billing_type,
      duration_days: p.duration_days || 30, trips_included: p.trips_included || 10, is_active: p.is_active,
    });
  };
  const deletePlan = async (id) => {
    if (!window.confirm("¿Eliminar este plan?")) return;
    await api.delete(`/admin/plans/${id}`); refresh();
  };
  const togglePlanActive = async (p) => {
    await api.put(`/admin/plans/${p.id}`, { ...p, is_active: !p.is_active }); refresh();
  };

  const doAssign = async () => {
    if (!assign.driver_id || !assign.plan_id) return;
    await api.post("/admin/subscriptions/assign", assign);
    setAssign({ driver_id: "", plan_id: "" }); refresh();
  };
  const approve = async (id) => { await api.post(`/admin/subscriptions/${id}/approve`); refresh(); };
  const cancelSub = async (id) => { await api.post(`/admin/subscriptions/${id}/cancel`); refresh(); };

  return (
    <div className="fade-in space-y-6" data-testid="admin-subscriptions-tab">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-black">Suscripciones</h1>
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none" data-testid="subs-required-toggle" onClick={toggleRequired}>
          <span>Exigir suscripción para operar</span>
          <span className={`w-11 h-6 rounded-full transition-colors relative ${required ? "bg-emerald-500" : "bg-slate-300"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${required ? "left-[22px]" : "left-0.5"}`} />
          </span>
          <span className={required ? "text-emerald-600" : "text-slate-400"}>{required ? "Sí" : "No"}</span>
        </label>
      </div>

      {/* Plan form */}
      <form onSubmit={submitPlan} className="card space-y-3" data-testid="plan-form">
        <h2 className="font-display font-bold text-lg">{editingId ? "Editar plan" : "Nuevo plan"}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="label">Nombre</label><input className="input-field" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name-input" /></div>
          <div><label className="label">Precio (€)</label><input className="input-field" type="number" step="0.01" min="0" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="plan-price-input" /></div>
        </div>
        <div><label className="label">Descripción</label><input className="input-field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="plan-description-input" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Tipo</label>
            <select className="input-field" value={form.billing_type} onChange={(e) => setForm({ ...form, billing_type: e.target.value })} data-testid="plan-type-select">
              <option value="monthly">Mensual (por días)</option>
              <option value="per_trips">Por cantidad de viajes</option>
            </select>
          </div>
          {form.billing_type === "monthly" ? (
            <div><label className="label">Duración (días)</label><input className="input-field" type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} data-testid="plan-duration-input" /></div>
          ) : (
            <div><label className="label">Viajes incluidos</label><input className="input-field" type="number" min="1" value={form.trips_included} onChange={(e) => setForm({ ...form, trips_included: e.target.value })} data-testid="plan-trips-input" /></div>
          )}
        </div>
        {error && <div className="p-2 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
        <div className="flex gap-2">
          <button type="submit" className="btn-primary" data-testid="plan-submit-btn"><Plus size={16} className="inline mr-1" weight="bold" />{editingId ? "Guardar cambios" : "Crear plan"}</button>
          {editingId && <button type="button" className="btn-ghost" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancelar</button>}
        </div>
      </form>

      {/* Plans list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-list">
        {plans.length === 0 && <p className="text-slate-500">Aún no hay planes.</p>}
        {plans.map((p) => (
          <div key={p.id} className="card" data-testid={`plan-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg">{p.name}</div>
                <div className="text-xs text-slate-500">{p.description}</div>
              </div>
              <span className={`pill ${p.is_active ? "pill-online" : "pill-offline"}`}>{p.is_active ? "Activo" : "Inactivo"}</span>
            </div>
            <div className="text-2xl font-display font-black mt-2" style={{ color: "var(--primary)" }}>€{Number(p.price).toFixed(2)}</div>
            <div className="flex items-center gap-1 text-sm text-slate-600 mt-1">
              {p.billing_type === "monthly"
                ? <><CalendarBlank size={15} weight="duotone" /> Mensual · {p.duration_days} días</>
                : <><Path size={15} weight="duotone" /> {p.trips_included} viajes</>}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => editPlan(p)} className="btn-ghost text-sm flex-1" data-testid={`plan-edit-${p.id}`}>Editar</button>
              <button onClick={() => togglePlanActive(p)} className="btn-ghost text-sm flex-1" data-testid={`plan-toggle-${p.id}`}>{p.is_active ? "Desactivar" : "Activar"}</button>
              <button onClick={() => deletePlan(p.id)} className="text-red-600 hover:bg-red-50 rounded-lg p-2" data-testid={`plan-delete-${p.id}`}><Trash size={16} weight="duotone" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Assign a plan to a driver */}
      <div className="card space-y-3" data-testid="assign-sub-card">
        <h2 className="font-display font-bold text-lg">Asignar plan a un conductor</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select className="input-field" value={assign.driver_id} onChange={(e) => setAssign({ ...assign, driver_id: e.target.value })} data-testid="assign-driver-select">
            <option value="">Selecciona conductor…</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.email}</option>)}
          </select>
          <select className="input-field" value={assign.plan_id} onChange={(e) => setAssign({ ...assign, plan_id: e.target.value })} data-testid="assign-plan-select">
            <option value="">Selecciona plan…</option>
            {plans.filter((p) => p.is_active).map((p) => <option key={p.id} value={p.id}>{p.name} (€{Number(p.price).toFixed(2)})</option>)}
          </select>
          <button onClick={doAssign} disabled={!assign.driver_id || !assign.plan_id} className="btn-primary" data-testid="assign-submit-btn">Asignar y activar</button>
        </div>
      </div>

      {/* Driver subscriptions */}
      <div className="card overflow-x-auto" style={{ padding: 0 }}>
        <table className="w-full min-w-[640px]" data-testid="driver-subs-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="px-4 py-3 text-left">Conductor</th><th className="px-4 py-3 text-left">Plan</th><th className="px-4 py-3 text-left">Estado</th><th className="px-4 py-3 text-left">Detalle</th><th className="px-4 py-3 text-right">Acciones</th></tr>
          </thead>
          <tbody>
            {subs.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-slate-500">Sin suscripciones</td></tr>}
            {subs.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`sub-row-${s.id}`}>
                <td className="px-4 py-3 font-bold">{s.driver_name}<div className="text-xs font-normal text-slate-500">{s.driver_email}</div></td>
                <td className="px-4 py-3 text-sm">{s.plan_snapshot?.name}</td>
                <td className="px-4 py-3"><span className={`pill ${STATUS_PILL(s.status)}`}>{s.status}{s.is_active ? "" : s.status === "active" ? " (vencido)" : ""}</span></td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {s.billing_type === "per_trips" ? `${s.trips_remaining ?? 0} viajes restantes` : s.expires_at ? `Vence ${new Date(s.expires_at).toLocaleDateString()}` : "—"}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {s.status === "pending" && <button onClick={() => approve(s.id)} className="text-emerald-600 hover:bg-emerald-50 rounded-lg p-2" title="Aprobar" data-testid={`approve-sub-${s.id}`}><CheckCircle size={18} weight="duotone" /></button>}
                  {s.status !== "cancelled" && <button onClick={() => cancelSub(s.id)} className="text-red-600 hover:bg-red-50 rounded-lg p-2" title="Cancelar" data-testid={`cancel-sub-${s.id}`}><X size={18} weight="bold" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
